import qs from "qs";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceStatus } from "@/models/enums/service";
import { AuthStatus } from "@/models/enums/auth";
import { isNotEmpty, mergeObjects } from "@/utils/utils";
import { UserModel, SaleOrgModel } from "@/models/classes/user";
import { OrganizeModel } from "@/models/classes/organize";
import Service from "@/services";
import { CompanyModel } from "@/models/classes/organize/company";
import { ChannelModel } from "@/models/classes/organize/channel";
import { SalesModel } from "@/models/classes/organize/sales";
import { Cookie } from "@/utils/cookies/cookie";
import { MenuCallPlanItem } from "@/models/classes/plan/visit";
import { CenterModel } from "@/models/classes/organize/center";
// import { MenuCallPlan } from '@/models/classes/plan/visit';

const service = new Service.AuthService();

// Interface
interface AuthState {
  auth: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    status: AuthStatus;
    token?: string;
    user: UserModel | null;
    saleOrg: SaleOrgModel | null | undefined;
  };
  organize: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: OrganizeModel | null;
    selected: {
      company: CompanyModel;
      channel: ChannelModel;
      sale: SalesModel;
      center: CenterModel;
    };
    open: boolean;
    isShowed: boolean;
    updated: number;
  };
  goodHistory: {
    open: boolean;
  };
  callplanMenu: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: MenuCallPlanItem[];
  };
  takeOrderMenu: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: MenuCallPlanItem[];
  };
  permissionMenu: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: MenuCallPlanItem[];
  }



}

// Initialize State
const initialState: AuthState = {
  auth: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    status: AuthStatus.unknow,
    token: undefined,
    user: null,
    saleOrg: null,
  },
  organize: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
    selected: {
      company: new CompanyModel(),
      channel: new ChannelModel(),
      sale: new SalesModel(),
      center: new CenterModel(),
    },
    open: false,
    isShowed: false,
    updated: 0,
  },
  goodHistory: {
    open: false,
  },
  callplanMenu: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
  takeOrderMenu: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
  permissionMenu: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
};

// Reducer
export const slice = createSlice({
  name: "Authorization",
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
    openOrganize: (state, action) => {
      state.organize.open = action.payload;
    },
    authInit: (state) => {
      const urlToken = new URLSearchParams(window.location.search)?.get("accessToken");
      const queryToken = qs.parse(window.location.hash.split("?")[1], {
        ignoreQueryPrefix: true,
      })?.accessToken;
      const localToken = localStorage.getItem("accessToken");

      let token: string | undefined = undefined;
      if (isNotEmpty(urlToken) && typeof urlToken == "string") {
        token = urlToken;
      } else if (isNotEmpty(queryToken) && typeof queryToken == "string") {
        token = queryToken;
      } else if (isNotEmpty(localToken) && typeof localToken == "string") {
        token = localToken;
      } else {
        token = undefined;
      }

      if (isNotEmpty(token) && token != undefined) {
        localStorage.setItem("accessToken", token);
        state.auth.token = token;
      } else {
        localStorage.clear();
        state.auth.status = AuthStatus.unauthenticated;
      }
    },
    authLogout: (_) => {
      localStorage.clear();
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    // Fetch User
    builder.addCase(fetchUser.pending, (state) => {
      localStorage.removeItem("user");
      localStorage.removeItem("saleOrgData");
      state.auth.network.code = ServiceStatus.loading;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.auth.network.code = ServiceStatus.succeeded;
      const user = action.payload;
      if (state.auth.token != null && user != undefined) {
        state.auth.status = AuthStatus.authenticated;
        state.auth.user = user.user;
        state.auth.saleOrg = user.saleOrg;
        localStorage.setItem("user", JSON.stringify(user.user));
        localStorage.setItem("saleOrgData", JSON.stringify(user.saleOrg));
        if (user.saleOrg && user?.saleOrg?.selectedCompanyName && user.saleOrg.selectedChannelName && user.saleOrg.selectedSaleOrgCriteriaId && user.saleOrg.selectedSaleOrgCenterId) {
          Cookie.setOrganize(user.saleOrg.selectedCompanyName, user.saleOrg.selectedChannelName, user.saleOrg.selectedSaleOrgCriteriaId, user.saleOrg.selectedSaleOrgCenterId);
        }
      } else {
        state.auth.status = AuthStatus.unknow;
        state.auth.user = null;
        state.auth.saleOrg = null;
      }
    });

    builder.addCase(fetchUser.rejected, (state, action) => {
      state.auth.network.code = ServiceStatus.failed;
      state.auth.network.error = (action.payload as string) || "Failed to fetch data";

      state.auth.status = AuthStatus.unauthenticated;
    });

    //MARK: Fetch Organization
    builder.addCase(fetchOrganize.pending, (state) => {
      state.organize.network.code = ServiceStatus.loading;
    });
    builder.addCase(fetchOrganize.fulfilled, (state, action) => {
      state.organize.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        if (state.organize.data == null) {
          const data = action.payload;
          const cookie = Cookie.getOrganize();

          const companies = data.data?.companies || [];
          const channels = data.data?.channels || [];
          const sales = data.data?.sales || [];
          const centers = data.data?.centers || [];

          const selected = {
            company: cookie?.company && companies.length > 0 ? companies.find((e: any) => e.value == cookie.company) || companies[0] : companies[0] || new CompanyModel("", ""),

            channel: cookie?.channel && channels.length > 0 ? channels.find((e: any) => e.value == cookie.channel) || channels[0] : channels[0] || new ChannelModel("", ""),

            sale: cookie?.sale && sales.length > 0 ? sales.find((e: any) => e.value == cookie.sale) || sales[0] : sales[0] || new SalesModel("", 0),

            center: cookie?.center && centers.length > 0 ? centers.find((e: any) => e.value == cookie.center) || centers[0] : centers[0] || new CenterModel("", 0),
          };

          // Check if all selections match the first item in their respective arrays
          const shouldRemoveCookie = companies.length > 0 && channels.length > 0 && sales.length > 0 && centers.length > 0 && selected.company?.value === companies[0]?.value && selected.channel?.value === channels[0]?.value && selected.sale?.value === sales[0]?.value && selected.center?.value === centers[0]?.value;

          if (shouldRemoveCookie) {
            Cookie.removeOrganize();
          }

          state.organize.selected = selected;
          // First Load and Update
          if (!state.organize.updated) {
            state.organize.updated = 1;
          }
        }
        state.organize.data = action.payload.data;
      }
    });
    builder.addCase(fetchOrganize.rejected, (state, action) => {
      state.organize.network.code = ServiceStatus.failed;
      state.organize.network.error = (action.payload as string) || "Failed to fetch data";
    });

    // Update Organization
    builder.addCase(updateOrganize.fulfilled, (state, action) => {
      if (action.payload && action.payload.isSuccess) {
        state.organize.updated += 1;

        const data = action.payload;
        Cookie.setOrganize(data.company.value, data.channel.value, data.sale.value, data.center.value);
        state.organize.selected = {
          company: data.company,
          channel: data.channel,
          sale: data.sale,
          center: data.center,
        };
      }
    });

    builder.addCase(fetchSaleOrgCenter.pending, (state) => {
      state.organize.network.code = ServiceStatus.loading;
    });
    builder.addCase(fetchSaleOrgCenter.fulfilled, (state, action) => {
      state.organize.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        const data = action.payload;
        const cookie = Cookie.getOrganize();
      }
    });
    builder.addCase(fetchSaleOrgCenter.rejected, (state, action) => {
      state.organize.network.code = ServiceStatus.failed;
      state.organize.network.error = (action.payload as string) || "Failed to fetch data";
    });

    //MARK: call plan menu
    builder.addCase(onFetchMenuCallPlan.pending, (state) => {
      state.callplanMenu.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchMenuCallPlan.fulfilled, (state, action) => {
      state.callplanMenu.network.code = ServiceStatus.succeeded;
      if (action.payload != null && action.payload) {
        state.callplanMenu.data = action.payload;
      }
    });
    builder.addCase(onFetchMenuCallPlan.rejected, (state, action) => {
      state.callplanMenu.network.code = ServiceStatus.failed;
      state.callplanMenu.network.error = (action.payload as string) || "Failed to fetch data";
    });

    //MARK: take order menu
    builder.addCase(onFetchMenuTakeOrder.pending, (state) => {
      state.takeOrderMenu.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchMenuTakeOrder.fulfilled, (state, action) => {
      state.takeOrderMenu.network.code = ServiceStatus.succeeded;
      if (action.payload != null && action.payload) {
        state.takeOrderMenu.data = action.payload;
      }
    });
    builder.addCase(onFetchMenuTakeOrder.rejected, (state, action) => {
      state.takeOrderMenu.network.code = ServiceStatus.failed;
      state.takeOrderMenu.network.error = (action.payload as string) || "Failed to fetch data";
    });

    //MARK: permission menu
    builder.addCase(onFetchMenuPermission.pending, (state) => {
      state.permissionMenu.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchMenuPermission.fulfilled, (state, action) => {
      state.permissionMenu.network.code = ServiceStatus.succeeded;
      if (action.payload != null && action.payload) {
        state.permissionMenu.data = action.payload;
      }
    });
    builder.addCase(onFetchMenuPermission.rejected, (state, action) => {
      state.permissionMenu.network.code = ServiceStatus.failed;
      state.permissionMenu.network.error = (action.payload as string) || "Failed to fetch data";
    });
  },
});

// Service
export const fetchUser = createAsyncThunk("auth/user", async (_, thunkAPI) => {
  try {
    const user = await service.getUser();
    return user;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//MARK: Organize
export const fetchOrganize = createAsyncThunk("organize/fetch", async (payload: { company?: string | null; channel?: string | null; saleOrg?: string | null } | undefined, thunkAPI) => {
  try {
    const company = payload?.company;
    const channel = payload?.channel;
    const saleOrg = payload?.saleOrg;

    let orgazination = await service.fetchOrganize(company, channel, saleOrg);
    if (!company && orgazination && orgazination.data) {
      let cookie = Cookie.getOrganize();
      if (orgazination.selectedValue) {
        Cookie.setOrganize(orgazination.selectedValue.company, orgazination.selectedValue.channel, orgazination.selectedValue.saleOrg, orgazination.selectedValue.center);
        cookie = Cookie.getOrganize();
      }

      const company = cookie.company ? orgazination.data.companies.find((e) => e.value == cookie.company) ?? orgazination.data.companies[0] : orgazination.data.companies[0];
      orgazination = await service.fetchOrganize(company.value);
      if (orgazination) {
        const channel = cookie.channel ? orgazination.data.channels.find((e) => e.value == cookie.channel) ?? orgazination.data.channels[0] : orgazination.data.channels[0];
        orgazination = await service.fetchOrganize(company.value, channel.value);
        if (orgazination) {
          const sale = cookie.sale ? orgazination.data.sales.find((e) => e.value == cookie.sale) ?? orgazination.data.sales[0] : orgazination.data.sales[0];
          orgazination = await service.fetchOrganize(company.value, channel.value, sale.value.toString());
        }
      }
    }
    return orgazination;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const updateOrganize = createAsyncThunk("organize/update", async (payload: { company: CompanyModel; channel: ChannelModel; sale: SalesModel; center: CenterModel }, thunkAPI) => {
  try {
    const isSuccess = await service.updateOrganize(payload.company.value, payload.channel.value, payload.sale.value, payload.center.value);
    return {
      isSuccess,
      company: payload.company,
      channel: payload.channel,
      sale: payload.sale,
      center: payload.center,
    };
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const fetchSaleOrgCenter = createAsyncThunk("organize/saleorg/center", async (params: { SaleOrgCriteriaId: number }, thunkAPI) => {
  try {
    const apiData = await service.fetchSaleOrgCenter(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
export const onFetchMenuCallPlan = createAsyncThunk("menu/callplan", async (params: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchMenuCallPlan(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const onFetchMenuTakeOrder = createAsyncThunk("menu/takeorder", async (params: any = {}, thunkAPI) => {
  try {
    const apiData = await service.fetchMenuTakeOrder(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const onFetchMenuPermission = createAsyncThunk("menu/permission", async (params: { type: string }, thunkAPI) => {
  try {
    const apiData = await service.fetchMenuPermission(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
// Actions
export const { reset, patch, authInit, authLogout, openOrganize } = slice.actions;

// Export
export default slice.reducer;
